<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :color="color"
    minimized-width="0"
    class="sidebar-container"
  >
    <div class="sidebar-content">
      <VaAccordion v-model="value" multiple>
        <VaCollapse v-for="(route, index) in visibleRoutes" :key="index">
          <template #header="{ value: isCollapsed }">
            <VaSidebarItem
              :to="route.children ? undefined : { name: route.name }"
              :active="routeHasActiveChild(route)"
              :active-color="activeColor"
              :text-color="textColor(route)"
              :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
              role="button"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-4">
                <VaIcon
                  v-if="route.meta.icon"
                  aria-hidden="true"
                  :name="route.meta.icon"
                  size="20px"
                  :color="iconColor(route)"
                />
                <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                  {{ t(route.displayName) }}
                  <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </template>

          <template #body>
            <div v-for="(childRoute, index2) in route.children" :key="index2">
              <VaSidebarItem
                :to="{ name: childRoute.name }"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Visit ${t(route.displayName)}`"
                hover-opacity="0.10"
              >
                <VaSidebarItemContent class="py-3 pr-2 pl-11">
                  <VaSidebarItemTitle class="leading-5 font-semibold">
                    {{ t(childRoute.displayName) }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </template>
        </VaCollapse>
      </VaAccordion>
    </div>
    <div class="sidebar-footer">
      <VaDivider />
      <div class="footer-inner">
        <VaIcon name="mso-account_circle" size="20px" class="mr-2" />
        <div class="truncate">
          <div class="label">Aktif Kullanıcı</div>
          <div class="name" :title="activeUserName">{{ activeUserName }}</div>
        </div>
      </div>
    </div>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useUserStore } from '../../stores/user'
import { Permission } from '../../permissions'
import { canSee, pkgStamp } from '../../composables/usePackageGate'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const store = useUserStore()
    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) return route.path.endsWith(`${section.name}`)
      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const hasPerm = (p?: string) => !p || store.permissions.includes(p as any)

    function filterByAccess(
      items: INavigationRoute[],
      canSeeFn: (key: NonNullable<INavigationRoute['pkg']>) => boolean,
      hasPermFn: (perm: Permission) => boolean,
    ): INavigationRoute[] {
      const walk = (list: INavigationRoute[]): INavigationRoute[] =>
        list
          .map((it) => {
            if (it.perm && !hasPermFn(it.perm)) return null
            if (it.pkg && !canSeeFn(it.pkg)) return null
            const children = it.children ? walk(it.children) : undefined
            if (children && children.length) return { ...it, children }
            return { ...it, children: undefined }
          })
          .filter((x): x is INavigationRoute => Boolean(x))
      return walk(items)
    }

    const visibleRoutes = computed(() => {
      void pkgStamp.value
      void store.permissions.length
      return filterByAccess(navigationRoutes.routes, canSee, hasPerm)
    })

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((r: INavigationRoute) => routeHasActiveChild(r)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    const activeUserName = computed(() => store.fullName || '—')

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      visibleRoutes,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      activeUserName,
    }
  },
})
</script>

<style scoped>
.sidebar-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
}

.sidebar-footer {
  flex-shrink: 0;
}

.footer-inner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.03);
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.footer-inner .label {
  font-size: 11px;
  opacity: 0.6;
  line-height: 1;
  margin-bottom: 2px;
}

.footer-inner .name {
  font-weight: 600;
  line-height: 1.2;
  max-width: 200px;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
